import { useEffect, useRef, useState } from 'react'
import { Plus } from 'lucide-react'
import { cn } from '@/components/ui/cn'

interface ExpandableFabProps {
  label: string
  onAction: () => void
  testId?: string
  className?: string
}

export function ExpandableFab({ label, onAction, testId, className }: ExpandableFabProps) {
  const [expanded, setExpanded] = useState(false)
  const fabRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!expanded) return

    const collapse = () => setExpanded(false)
    const onPointerDown = (e: PointerEvent) => {
      if (fabRef.current && !fabRef.current.contains(e.target as Node)) {
        collapse()
      }
    }

    const timeoutId = window.setTimeout(collapse, 4000)
    document.addEventListener('pointerdown', onPointerDown)
    return () => {
      window.clearTimeout(timeoutId)
      document.removeEventListener('pointerdown', onPointerDown)
    }
  }, [expanded])

  const handleClick = () => {
    if (!expanded) {
      setExpanded(true)
      return
    }
    setExpanded(false)
    onAction()
  }

  return (
    <div
      ref={fabRef}
      className={cn(
        'fixed z-40 flex items-center lg:hidden',
        'bottom-[calc(4.5rem+env(safe-area-inset-bottom))] right-4',
        className,
      )}
      data-testid={testId}
    >
      <button
        type="button"
        onClick={handleClick}
        aria-label={expanded ? label : 'Expand add action'}
        data-testid="vocabulary-add-fab-button"
        className={cn(
          'flex h-14 items-center justify-center bg-primary text-primary-foreground shadow-lg transition-all duration-200',
          expanded
            ? 'gap-2 rounded-full px-5'
            : 'w-14 rounded-full',
        )}
      >
        <Plus className={cn('h-6 w-6 shrink-0 transition-transform', expanded && 'rotate-45')} />
        {expanded ? <span className="whitespace-nowrap text-sm font-semibold">{label}</span> : null}
      </button>
    </div>
  )
}
